/**
 * Extract open house data from property objects and API responses
 * This function searches through various possible locations where open house data might be stored
 * @param prop - Property object that might contain open house data
 * @param data - Optional additional data object (e.g., from API response)
 * @param shouldLogDetailed - Whether to show detailed logging (default: false)
 * @returns Array of open house data objects
 */
export function extractOpenHouseData(prop: any, data?: any, shouldLogDetailed: boolean = false): any[] {

  let openHouses: any[] = [];

  // Try multiple possible locations for open house data
  const possiblePaths = [
    // Direct property paths
    prop?.openHouses,
    prop?.openHouse,
    prop?.openhouse,
    prop?.open_houses,
    prop?.open_house,
    
    // Nested paths
    prop?.listingMetadata?.openHouses,
    prop?.listingMetadata?.openHouse,
    prop?.listingInfo?.openHouses,
    prop?.listingInfo?.openHouse,
    prop?.propertyInfo?.openHouses,
    prop?.propertyInfo?.openHouse,
    
    // Event-related paths
    prop?.events,
    prop?.scheduledEvents,
    prop?.openHouseEvents,
    prop?.openHouseSchedules,
    
    // Data object paths (for rich data responses)
    data?.openHouses,
    data?.openHouse,
    data?.openhouse,
    data?.open_houses,
    data?.open_house,
    data?.events,
    data?.scheduledEvents,
    data?.openHouseEvents,
    data?.openHouseSchedules,
    
    // Main house info paths
    data?.mainHouseInfoPanelInfo?.mainHouseInfo?.openHouses,
    data?.mainHouseInfoPanelInfo?.mainHouseInfo?.openHouse,
    data?.mainHouseInfoPanelInfo?.mainHouseInfo?.events,
    
    // Above the fold paths
    data?.aboveTheFold?.openHouses,
    data?.aboveTheFold?.openHouse,
    data?.aboveTheFold?.events,
    
    // Listing info paths
    data?.listingInfo?.openHouses,
    data?.listingInfo?.openHouse,
    data?.listingInfo?.events,
  ];



  // Check each possible path
  for (let i = 0; i < possiblePaths.length; i++) {
    const path = possiblePaths[i];
    const pathName = [
      'prop.openHouses', 'prop.openHouse', 'prop.openhouse', 'prop.open_houses', 'prop.open_house',
      'prop.listingMetadata.openHouses', 'prop.listingMetadata.openHouse', 'prop.listingInfo.openHouses', 'prop.listingInfo.openHouse', 'prop.propertyInfo.openHouses', 'prop.propertyInfo.openHouse',
      'prop.events', 'prop.scheduledEvents', 'prop.openHouseEvents', 'prop.openHouseSchedules',
      'data.openHouses', 'data.openHouse', 'data.openhouse', 'data.open_houses', 'data.open_house', 'data.events', 'data.scheduledEvents', 'data.openHouseEvents', 'data.openHouseSchedules',
      'data.mainHouseInfoPanelInfo.mainHouseInfo.openHouses', 'data.mainHouseInfoPanelInfo.mainHouseInfo.openHouse', 'data.mainHouseInfoPanelInfo.mainHouseInfo.events',
      'data.aboveTheFold.openHouses', 'data.aboveTheFold.openHouse', 'data.aboveTheFold.events',
      'data.listingInfo.openHouses', 'data.listingInfo.openHouse', 'data.listingInfo.events'
    ][i];
    
    if (path && (Array.isArray(path) || typeof path === 'object')) {
      // Handle different data formats
      if (Array.isArray(path)) {
        openHouses = path;
      } else if (typeof path === 'object') {
        // If it's an object, try to extract array from it
        if (path.events && Array.isArray(path.events)) {
          openHouses = path.events;
        } else if (path.openHouses && Array.isArray(path.openHouses)) {
          openHouses = path.openHouses;
        } else if (path.openHouse && Array.isArray(path.openHouse)) {
          openHouses = path.openHouse;
        } else {
          // If it's a single open house object, wrap it in an array
          openHouses = [path];
        }
      }
      
      if (openHouses.length > 0) {
        break;
      }
    }
  }

  // Search recursively for open house related fields if not found
  if (openHouses.length === 0) {
    
    const searchForOpenHouseFields = (obj: any, path: string = ''): any[] => {
      if (obj && typeof obj === 'object') {
        for (const key in obj) {
          const currentPath = path ? `${path}.${key}` : key;
          const value = obj[key];
          
          if (key.toLowerCase().includes('open') && key.toLowerCase().includes('house')) {
            
            if (Array.isArray(value)) {
              return value;
            } else if (typeof value === 'object' && value !== null) {
              const result = searchForOpenHouseFields(value, currentPath);
              if (result.length > 0) return result;
            }
          } else if (key.toLowerCase().includes('event') && key.toLowerCase().includes('schedule')) {
            
            if (Array.isArray(value)) {
              return value;
            } else if (typeof value === 'object' && value !== null) {
              const result = searchForOpenHouseFields(value, currentPath);
              if (result.length > 0) return result;
            }
          } else if (typeof value === 'object' && value !== null) {
            const result = searchForOpenHouseFields(value, currentPath);
            if (result.length > 0) return result;
          }
        }
      }
      return [];
    };

    // Search in both prop and data objects
    openHouses = searchForOpenHouseFields(prop);
    
    if (openHouses.length === 0) {
      openHouses = searchForOpenHouseFields(data);
    }
  }

  // Normalize open house data format
  if (openHouses.length > 0) {
    openHouses = openHouses.map((oh: any, index: number) => {
      
              // Handle different open house data formats
        if (typeof oh === 'string') {
          // If it's a string, try to parse it as a date
          const date = new Date(oh);
          if (!isNaN(date.getTime())) {
            const normalized = {
              startTime: oh,
              endTime: oh,
              date: oh
            };
            return normalized;
          }
          const normalized = { description: oh };
          return normalized;
        } else if (typeof oh === 'object' && oh !== null) {
        // Normalize object format
        const normalized = {
          startTime: oh.startTime || oh.start || oh.openHouseStart || oh.startTime,
          endTime: oh.endTime || oh.end || oh.openHouseEnd || oh.endTime,
          date: oh.date || oh.startDate || oh.openHouseDate,
          description: oh.description || oh.notes || oh.comments,
          type: oh.type || 'open_house',
          ...oh // Include any other properties
        };
        return normalized;
      }
      return oh;
    });
  }

  return openHouses;
} 